/**
 * The canonical permission list for the entire platform.
 *
 * MASTER_DEVELOPMENT_PROMPT.md and Task 04 both state that this list is
 * canonical and that Task 02 seeds exactly it. Holding it as code — in one
 * place, consumed by the seeder, the RBAC middleware and every guarded route
 * — is what makes that claim enforceable rather than aspirational.
 *
 * SPEC CORRECTION (SPEC_CORRECTIONS.md #1): Tasks 07, 09 and 19 each used
 * permissions absent from Task 04's list (`Property.Create/Update/Delete`,
 * `Guest.Delete`, `Payment.Write/Capture/Void`). Every guarded endpoint in
 * those tasks would have checked a permission row that was never seeded.
 * They are included below and Task 04's list has been amended to match.
 *
 * Adding a permission means adding it here and shipping the seed migration
 * that inserts it. There is no other correct order.
 */
export const PERMISSIONS = {
    // --- Property (Task 07) ---
    PROPERTY_READ: 'Property.Read',
    PROPERTY_WRITE: 'Property.Write',
    PROPERTY_CREATE: 'Property.Create',
    PROPERTY_UPDATE: 'Property.Update',
    PROPERTY_DELETE: 'Property.Delete',
    PROPERTY_CONFIGURE: 'Property.Configure',
    PROPERTY_SETUP: 'Property.Setup',
    // --- Property groups (Task 07 extension) ---
    PROPERTY_GROUP_READ: 'PropertyGroup.Read',
    PROPERTY_GROUP_CREATE: 'PropertyGroup.Create',
    PROPERTY_GROUP_UPDATE: 'PropertyGroup.Update',
    PROPERTY_GROUP_DELETE: 'PropertyGroup.Delete',
    // --- Amenity catalogue (shared by property and room modules) ---
    AMENITY_READ: 'Amenity.Read',
    AMENITY_CREATE: 'Amenity.Create',
    AMENITY_UPDATE: 'Amenity.Update',
    AMENITY_DELETE: 'Amenity.Delete',
    // --- Inclusion catalogue (rate-shaped configuration, Task 11) ---
    INCLUSION_READ: 'Inclusion.Read',
    INCLUSION_CREATE: 'Inclusion.Create',
    INCLUSION_UPDATE: 'Inclusion.Update',
    INCLUSION_DELETE: 'Inclusion.Delete',
    // --- Room and inventory (Task 08) ---
    ROOM_READ: 'Room.Read',
    ROOM_CREATE: 'Room.Create',
    ROOM_UPDATE: 'Room.Update',
    ROOM_DELETE: 'Room.Delete',
    INVENTORY_READ: 'Inventory.Read',
    INVENTORY_UPDATE: 'Inventory.Update',
    // --- Room type catalogue (Task 08) ---
    ROOM_TYPE_READ: 'RoomType.Read',
    ROOM_TYPE_CREATE: 'RoomType.Create',
    ROOM_TYPE_UPDATE: 'RoomType.Update',
    ROOM_TYPE_DELETE: 'RoomType.Delete',
    // --- Availability (Task 10) ---
    AVAILABILITY_READ: 'Availability.Read',
    AVAILABILITY_UPDATE: 'Availability.Update',
    RESTRICTION_READ: 'Restriction.Read',
    RESTRICTION_UPDATE: 'Restriction.Update',
    // --- Pricing (Task 11) ---
    PRICING_READ: 'Pricing.Read',
    PRICING_UPDATE: 'Pricing.Update',
    EXCHANGE_RATE_READ: 'ExchangeRate.Read',
    EXCHANGE_RATE_CREATE: 'ExchangeRate.Create',
    EXCHANGE_RATE_UPDATE: 'ExchangeRate.Update',
    EXCHANGE_RATE_DELETE: 'ExchangeRate.Delete',
    PROMOTION_READ: 'Promotion.Read',
    PROMOTION_CREATE: 'Promotion.Create',
    PROMOTION_UPDATE: 'Promotion.Update',
    PROMOTION_DELETE: 'Promotion.Delete',
    // --- Policies (rate-shaped configuration, Task 11 tables) ---
    // Delete is its own permission rather than riding on Update: correcting a
    // typo and destroying the policy every rate plan points at are not the same
    // act, and a rate plan whose policy vanished keeps selling with none.
    CANCELLATION_POLICY_READ: 'CancellationPolicy.Read',
    CANCELLATION_POLICY_CREATE: 'CancellationPolicy.Create',
    CANCELLATION_POLICY_UPDATE: 'CancellationPolicy.Update',
    CANCELLATION_POLICY_DELETE: 'CancellationPolicy.Delete',
    PAYMENT_POLICY_READ: 'PaymentPolicy.Read',
    PAYMENT_POLICY_CREATE: 'PaymentPolicy.Create',
    PAYMENT_POLICY_UPDATE: 'PaymentPolicy.Update',
    PAYMENT_POLICY_DELETE: 'PaymentPolicy.Delete',
    // --- Tax (Task 11 admin CRUD) ---
    TAX_READ: 'Tax.Read',
    TAX_CREATE: 'Tax.Create',
    TAX_UPDATE: 'Tax.Update',
    TAX_DELETE: 'Tax.Delete',
    // --- Reservation (Task 12) ---
    RESERVATION_READ: 'Reservation.Read',
    RESERVATION_WRITE: 'Reservation.Write',
    RESERVATION_CANCEL: 'Reservation.Cancel',
    RESERVATION_MODIFY: 'Reservation.Modify',
    RESERVATION_DASHBOARD: 'Reservation.Dashboard.Read',
    // Task 31: booking attempt capture — distinct from Reservation.Read
    // because failed attempts may have no reservation at all.
    BOOKING_ATTEMPT_READ: 'BookingAttempt.Read',
    BOOKING_ATTEMPT_FOLLOWUP: 'BookingAttempt.Followup',
    BOOKING_ATTEMPT_STATISTICS_READ: 'BookingAttempt.Statistics.Read',
    // --- Guest (Task 09) ---
    GUEST_READ: 'Guest.Read',
    GUEST_WRITE: 'Guest.Write',
    GUEST_DELETE: 'Guest.Delete',
    // Task 30: distinct from Guest.Read — reading a guest record and reading
    // a guest's personal data in the clear are different acts.
    GUEST_PII_READ: 'Guest.Pii.Read',
    // --- Payment (Tasks 16, 19) ---
    PAYMENT_READ: 'Payment.Read',
    PAYMENT_WRITE: 'Payment.Write',
    PAYMENT_CAPTURE: 'Payment.Capture',
    PAYMENT_REFUND: 'Payment.Refund',
    PAYMENT_VOID: 'Payment.Void',
    PAYMENT_CONFIGURE: 'Payment.Configure',
    // --- Identity (Task 04) ---
    USER_READ: 'User.Read',
    USER_WRITE: 'User.Write',
    ROLE_READ: 'Role.Read',
    ROLE_WRITE: 'Role.Write',
    PERMISSION_READ: 'Permission.Read',
    PERMISSION_WRITE: 'Permission.Write',
    AUDIT_READ: 'Audit.Read',
    MODULE_READ: 'Module.Read',
    // --- Business rules (Task 06) ---
    RULE_READ: 'Rule.Read',
    RULE_CREATE: 'Rule.Create',
    RULE_UPDATE: 'Rule.Update',
    RULE_DELETE: 'Rule.Delete',
    RULE_EXECUTE: 'Rule.Execute',
    // --- Notification (Task 20) ---
    NOTIFICATION_READ: 'Notification.Read',
    NOTIFICATION_SEND: 'Notification.Send',
    NOTIFICATION_CONFIGURE: 'Notification.Configure',
    // --- PMS and integration (Tasks 13, 15) ---
    PMS_READ: 'PMS.Read',
    PMS_CONFIGURE: 'PMS.Configure',
    PMS_SYNC: 'PMS.Sync',
    INTEGRATION_READ: 'Integration.Read',
    INTEGRATION_MANAGE: 'Integration.Manage',
    // --- PMS mapping administration (Task 33) ---
    PMS_MAPPING_READ: 'PmsMapping.Read',
    PMS_MAPPING_UPDATE: 'PmsMapping.Update',
    // --- Attachments ---
    ATTACHMENT_READ: 'Attachment.Read',
    ATTACHMENT_WRITE: 'Attachment.Write',
    ATTACHMENT_DELETE: 'Attachment.Delete',
    // --- Platform ---
    SYSTEM_CONFIGURE: 'System.Configure',
    // --- Platform reference data (countries, states, cities, currencies) ---
    // Read is broad on purpose: every property form, tenant form and guest form
    // needs the vocabularies, so a tenant user who cannot read them cannot fill
    // in an address. Write and Import are platform-operator permissions and are
    // granted beside the Tenant.* set — reference data is platform-wide, so a
    // tenant able to edit it edits it for everyone.
    //
    // Import is separate from Write because one call can rewrite thousands of
    // rows, and that is worth granting apart from the ability to edit one.
    REFERENCE_READ: 'Reference.Read',
    REFERENCE_WRITE: 'Reference.Write',
    REFERENCE_IMPORT: 'Reference.Import',
    // --- Tenant lifecycle (multi-tenancy) ---
    // Held only by platform operators who sit outside every tenant, never by a
    // tenant's own administrators — a tenant admin role must not scope to other
    // tenants. Granting any of these to a tenant-scoped role (e.g. via
    // ROLES.ADMINISTRATOR) would let that tenant create, suspend or
    // impersonate its way into other tenants' data, breaking row-level
    // isolation regardless of what the database enforces.
    TENANT_READ: 'Tenant.Read',
    TENANT_CREATE: 'Tenant.Create',
    TENANT_UPDATE: 'Tenant.Update',
    TENANT_SUSPEND: 'Tenant.Suspend',
    TENANT_DELETE: 'Tenant.Delete',
    TENANT_IMPERSONATE: 'Tenant.Impersonate',
    TENANT_USER_MANAGE: 'Tenant.UserManage',
    TENANT_MODULE_ASSIGN: 'Tenant.ModuleAssign',
};
/**
 * Guest-scoped permissions.
 *
 * These belong to the Guest role only and are enforced by ownership, never by
 * property scope. A guest token must fail every staff permission check by
 * construction — see Task 04's token audience isolation.
 */
export const GUEST_SELF_PERMISSIONS = {
    GUEST_SELF_READ: 'Guest.Self.Read',
    GUEST_SELF_WRITE: 'Guest.Self.Write',
    GUEST_SELF_RESERVATION_READ: 'Guest.Self.Reservation.Read',
    GUEST_SELF_RESERVATION_MODIFY: 'Guest.Self.Reservation.Modify',
    GUEST_SELF_RESERVATION_CANCEL: 'Guest.Self.Reservation.Cancel',
    GUEST_SELF_PAYMENT_READ: 'Guest.Self.Payment.Read',
};
/** Every permission the platform recognises. Task 02 seeds exactly this list. */
export const ALL_PERMISSIONS = [
    ...Object.values(PERMISSIONS),
    ...Object.values(GUEST_SELF_PERMISSIONS),
];
/** Canonical role names. Seeded by Task 02, enforced by Task 04. */
export const ROLES = {
    SUPER_ADMINISTRATOR: 'Super Administrator',
    ADMINISTRATOR: 'Administrator',
    PROPERTY_ADMINISTRATOR: 'Property Administrator',
    SUPPORT: 'Support',
    GUEST: 'Guest',
};
/**
 * Staff roles. Every one of these requires MFA (Task 04, PCI DSS 8.4.2,
 * ISO 27001 A.8.5). The Guest role is deliberately excluded — it is a
 * distinct principal type, not a low-privilege staff role.
 */
export const STAFF_ROLES = [
    ROLES.SUPER_ADMINISTRATOR,
    ROLES.ADMINISTRATOR,
    ROLES.PROPERTY_ADMINISTRATOR,
    ROLES.SUPPORT,
];
/** JWT audiences. A guest token can never satisfy a staff permission check. */
export const TOKEN_AUDIENCE = {
    STAFF: 'staff',
    GUEST: 'guest',
    RESERVATION: 'reservation',
};
//# sourceMappingURL=permissions.js.map